import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, HostBinding, OnInit } from "@angular/core";
import { BaseComponent } from "../../core/base.component";
import { SessionService } from "../../shared/services/session.service";
import { Router } from "@angular/router";
import { GlobalDataService } from "src/app/shared/services/global-data.service";
import { OutingService } from "src/app/outing/outing.service";
import { AuthService } from "@auth0/auth0-angular";
import { MediaMatcher } from "@angular/cdk/layout";
import { QueryFilter } from "src/app/shared/models/query-filter.model";
import { UserData } from "src/app/shared/models/user.model";
import { StringHelpers } from "src/app/shared/helpers/string-helpers";
import { OrganizationService } from "src/app/shared/services/identity/organization.service";
import { AccountService } from "src/app/account/account.service";
import { StripeSearchOptions } from "src/app/shared/models/stripe/stripe-search-options";
import { environment } from "src/environments/environment";
import { AlertModalComponent } from "src/app/shared/modals/alert-modal/alert-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { GtStripeService } from "src/app/shared/services/gt-stripe.service";

@Component({
  selector: "outing-layout",
  templateUrl: "./postLogin-outing-layout.component.html",
  styleUrls: ["../layout.component.scss"],
  standalone: false
})
export class PostLoginOutingLayoutComponent extends BaseComponent implements OnInit, AfterViewInit {
  public user: UserData = new UserData();
  public isDarkTheme = false;
  public isOutingSelected: boolean = false;
  public createFirstOuting: boolean = false;
  public isSkeletonLoading: boolean = true;
  public toggled: boolean = true;
  public firstOutingCreation = new EventEmitter();

  private _mobileQueryListener: () => void
  mobileQuery: MediaQueryList;

  constructor(
    public sessionService: SessionService,
    private router: Router,
    private globalDataService: GlobalDataService,
    private outingService: OutingService,
    public authService: AuthService,
    private organizationService: OrganizationService,
    private accountService: AccountService,
    private dialog: MatDialog,
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    stringHelpers: StringHelpers,
    private stripeService: GtStripeService
  ) {
    super(sessionService, stringHelpers);
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngAfterViewInit(): void {
    this.globalDataService.iterationSelectionEvent.subscribe(result => {
      this.selectedIteration = result;
      this.selectedOuting = this.getGlobalOuting();
      this.isOutingSelected = true;
    })
  }

  ngOnInit() {
    this.user = this.getUserFromSession();
    this.checkIfStripeOnboardingIsComplete();
  }

  checkIfStripeOnboardingIsComplete() {
    this.organizationService.getOrganizationById(this.user.organizationId).subscribe({
      next: (organization: any) => {
        if (organization.stripeAccountId) {
          this.stripeService.getAccountById(this.user.stripeAccountId).subscribe({
            next: (result: any) => {
              if (result.requirements.past_due.length === 0) {
                this.checkIfOutingIsSelected();
                return;
              }else{
                this.promptStripeOnboarding();
              }
            }
          });
        }else if(this.router.url.includes('my-account')){
          this.isSkeletonLoading = false;
          return;
        }
        else{
        this.checkProductVersion();
        }
      },
      error: (err) => {
        console.error('Error checking organization:', err);
        this.isSkeletonLoading = false;
      }
    });
  }

  private checkProductVersion() {
    const options: StripeSearchOptions = new StripeSearchOptions();
    options.query = `metadata['GolfTechId']:'${this.sessionService.getUserId()}'`;

    this.accountService.getStripeCustomer(options).subscribe({
      next: (result: any) => {
        const product = result.data[0]?.subscriptions?.data[0]?.items?.data[0]?.plan?.product;

        if (!product) {
          console.error('Unable to determine product version');
          this.checkIfOutingIsSelected();
          return;
        }

        // Check if they're on the free version
        const isFreeVersion = product === environment.stripe.outingPlatformFreeProductId;

        if (isFreeVersion) {
          // Free version - no payment collection needed
          this.checkIfOutingIsSelected();
        } else {
          // Paid version - they need to complete Stripe onboarding
          this.promptStripeOnboarding();
        }
      },
      error: (err) => {
        console.error('Error checking product version:', err);
        // On error, allow them to proceed
        this.checkIfOutingIsSelected();
      }
    });
  }

  private promptStripeOnboarding() {
    const dialogRef = this.dialog.open(AlertModalComponent, {
      width: '35vw',
      disableClose: true
    });

    dialogRef.componentInstance.disableClose = true;
    dialogRef.componentInstance.title = "Stripe Account Required";
    dialogRef.componentInstance.body = "In order to accept payments and register golfers, you need to complete the Stripe onboarding process. Click 'Continue' to set up your account under My Account.";
    dialogRef.componentInstance.submitText = "Continue";

    dialogRef.afterClosed().subscribe({
      next: () => {
        this.isSkeletonLoading = false;
        this.router.navigate(['/outing-management/my-account', this.user.id]);
      }
    });
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
  }

  checkIfOutingIsSelected() {
    this.isLoading = true;
    if (this.isGlobalOutingSet()) {
      this.selectedIteration = this.getGlobalOutingIteration();
      this.selectedOuting = this.getGlobalOuting();
      this.isOutingSelected = true;
      this.createFirstOuting = false;
      this.isLoading = false;
      this.isSkeletonLoading = false;
    } else {
      const filter = new QueryFilter();
      this.outingService.getOutingsByOrganization(this.user.organizationId, filter).subscribe(result => {
        if (result === null || result.results.length === 0) {
          this.createFirstOuting = true;
        } else {
          this.createFirstOuting = false;
        }
        this.isLoading = false;
        this.isSkeletonLoading = false;
      }, err => {
        console.log(err);
        this.isSkeletonLoading = false;
      });
    }
  }


  logout() {
    this.sessionService.removeUser();
    this.user = new UserData();
    this.router.navigate(["/login"]);
    this.authService.logout();
  }

  onToggleChange(event: any) {
    if (event.checked) {
      document.body.classList.add('dark-mode');
      this.isDarkTheme = true;
      this.user.defaultTheme = false;
      this.sessionService.setUser(this.user);
    } else {
      document.body.classList.remove('dark-mode');
      this.isDarkTheme = false;
      this.user.defaultTheme = true;
      this.sessionService.setUser(this.user);
    }
  }

  onIterationSelection(data) {
    this.setGlobalOutingIteration(data);
  }

  onFirstOutingCreation(data) {
    this.createFirstOuting = false;
    this.router.navigate['/'];
  }

}